import React, { useCallback, useEffect, useState } from "react";
import {
  ArrowDownToLine, ArrowUpFromLine, Boxes, Download, Loader2, PackageMinus,
  ScanLine, Search, ShoppingCart, Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ReceiveGoodsDialog } from "./inventory/ReceiveGoodsDialog";
import { StockAuditPanel } from "./inventory/StockAuditPanel";
import { Camera, CameraOff, PackageCheck, TrendingDown, Printer, Barcode } from "lucide-react";
import { printBarcodes, downloadBarcodeSheet } from "@/lib/barcode";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { downloadCSV, fdatetime, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState, LoadingRows } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/pages/crm/LeadForm";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

const REASONS_IN = ["Purchase order received", "Supplier invoice entry", "Manual stock entry", "Customer return", "Stock correction"];
const REASONS_OUT = ["Dispatched against invoice", "Sample / demo", "Damaged", "Manual stock entry", "Stock correction"];

export default function Inventory() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [reorder, setReorder] = useState([]);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [camOn, setCamOn] = useState(false);
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);

  const stopCam = React.useCallback(() => {
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
    setCamOn(false);
  }, []);

  const startCam = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      return toast.error("This device or browser does not allow camera access.");
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
      });
      streamRef.current = stream;
      setCamOn(true);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); } }, 60);
      if ("BarcodeDetector" in window) {
        const det = new window.BarcodeDetector({
          formats: ["code_128", "ean_13", "ean_8", "code_39", "upc_a", "qr_code"],
        });
        const tick = async () => {
          if (!streamRef.current || !videoRef.current) return;
          try {
            const found = await det.detect(videoRef.current);
            if (found?.length) {
              const code = found[0].rawValue;
              setScanCode(code);
              stopCam();
              try {
                const { data } = await api.get(`/inventory/barcode/${code}`);
                toast.success(`${data.product_name} . ${data.qty} in stock`);
                setScanOpen(false);
                setQ(data.sku);
              } catch { toast.error("Scanned code did not match any product."); }
              return;
            }
          } catch { /* keep trying */ }
          setTimeout(tick, 350);
        };
        tick();
      } else {
        toast.info("Live detection is not supported here. Point the camera and type the code below.");
      }
    } catch {
      toast.error("Camera permission was refused.");
    }
  };

  useEffect(() => () => stopCam(), [stopCam]);
  const [picked, setPicked] = useState([]);
  const [rows, setRows] = useState([]);
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [lowOnly, setLowOnly] = useState(false);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [moveOpen, setMoveOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanCode, setScanCode] = useState("");
  const [form, setForm] = useState({ product_id: "", direction: "in", qty: 1, reason: "Manual stock entry", reference: "", supplier: "", invoice_file: "", po_id: "", mark_fully_received: false });
  const [openPos, setOpenPos] = useState([]);
  const [audits, setAudits] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, mv, ro, au] = await Promise.all([
        api.get("/inventory", { params: { q, category, low_only: lowOnly } }),
        api.get("/inventory/moves"),
        api.get("/inventory/reorder").catch(() => ({ data: [] })),
        api.get("/stock-audits").catch(() => ({ data: [] })),
      ]);
      setRows(inv.data); setMoves(mv.data); setReorder(ro.data); setAudits(au.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [q, category, lowOnly]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/purchase-orders/open").then((r) => setOpenPos(r.data)).catch(() => {});
  }, []);

  const lowCount = rows.filter((r) => r.qty <= r.min_stock).length;
  const negative = rows.filter((r) => r.qty < 0);

  const openMove = (product_id, direction) => {
    setForm({ product_id, direction, qty: 1, reason: direction === "in" ? REASONS_IN[0] : REASONS_OUT[0], reference: "", supplier: "", invoice_file: "", po_id: "", mark_fully_received: false });
    setMoveOpen(true);
  };

  const selectedName = (rows.find((r) => r.product_id === form.product_id)?.product_name || "").trim().toLowerCase();
  const posForProduct = openPos.filter((p) => p.items?.some((i) => {
    if (i.pending_qty <= 0) return false;
    if (i.product_id) return i.product_id === form.product_id;
    return !!selectedName && (i.name || "").trim().toLowerCase() === selectedName;
  }));

  const submitMove = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/move", { ...form, qty: Number(form.qty) });
      toast.success(`Stock ${form.direction === "in" ? "added" : "issued"} successfully`
        + (data?.po_status ? ` \u00b7 PO marked ${data.po_status}` : ""));
      setMoveOpen(false);
      api.get("/purchase-orders/open").then((r) => setOpenPos(r.data)).catch(() => {});
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const scan = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.get(`/inventory/barcode/${scanCode.trim()}`);
      toast.success(`${data.product_name} . ${data.qty} in stock`);
      setScanOpen(false);
      setQ(data.sku);
      setScanCode("");
    } catch (err) { toast.error(errMsg(err, "No product found for that code.")); }
  };

  const columns = [
    {
      key: "product", label: "Product",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{r.product_name}</p>
          <p className="num truncate text-xs text-muted-foreground">{r.sku} . {r.barcode}</p>
        </div>
      ),
    },
    { key: "category", label: "Category", render: (r) => <span className="text-sm text-muted-foreground">{r.category}</span>, hideMobile: true },
    { key: "supplier", label: "Supplier", render: (r) => <span className="text-sm text-muted-foreground">{r.supplier || "\u2014"}</span>, hideMobile: true },
    {
      key: "qty", label: "Available", align: "right",
      render: (r) => (
        <div className="flex items-center justify-end gap-2">
          <span className={cn("num text-sm font-bold", r.qty < 0 ? "text-destructive" : r.qty <= r.min_stock ? "text-destructive" : "text-foreground")}>{r.qty}</span>
          {r.qty < 0
            ? <StatusBadge status="Negative" tone="danger" dot={false} className="text-[10px]" />
            : r.qty <= r.min_stock && <StatusBadge status="Low" tone="danger" dot={false} className="text-[10px]" />}
        </div>
      ),
    },
    { key: "min_stock", label: "Min level", align: "right", render: (r) => <span className="num text-sm text-muted-foreground">{r.min_stock}</span>, hideMobile: true },
    { key: "selling_price", label: "Selling price", align: "right", render: (r) => <span className="num text-sm">{money(r.selling_price)}</span> },
    {
      key: "actions", label: "", align: "right",
      render: (r) => can("inventory", "edit") ? (
        <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <Button size="iconSm" variant="outline" title="Stock in" onClick={() => openMove(r.product_id, "in")} data-testid={`stock-in-${r.sku}`}>
            <ArrowDownToLine className="h-4 w-4 text-success" />
          </Button>
          <Button size="iconSm" variant="outline" title="Stock out" onClick={() => openMove(r.product_id, "out")}>
            <ArrowUpFromLine className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ) : null,
    },
  ];

  const moveColumns = [
    {
      key: "number", label: "Reference",
      render: (r) => (
        <div className="min-w-0">
          <p className="num truncate text-sm font-semibold">{r.reference || r.po_number || "\u2014"}</p>
          <p className="num truncate text-[11px] text-muted-foreground">{r.number}</p>
        </div>
      ),
    },
    { key: "product_name", label: "Product", render: (r) => <span className="text-sm font-medium">{r.product_name}</span> },
    {
      key: "direction", label: "Movement",
      render: (r) => <StatusBadge status={r.direction === "in" ? "Stock In" : "Stock Out"} tone={r.direction === "in" ? "success" : "warning"} />,
    },
    { key: "qty", label: "Qty", align: "right", render: (r) => <span className="num text-sm font-semibold">{r.direction === "in" ? "+" : "-"}{r.qty}</span> },
    { key: "stock", label: "Stock", align: "right", render: (r) => <span className="num text-xs text-muted-foreground">{r.prev_qty} &rarr; {r.new_qty}</span>, hideMobile: true },
    { key: "reason", label: "Reason", render: (r) => <span className="text-sm text-muted-foreground">{r.reason}</span>, hideMobile: true },
    { key: "staff", label: "By", align: "right", render: (r) => <span className="text-xs text-muted-foreground">{r.staff}<br />{fdatetime(r.created_at)}</span> },
  ];

  const reorderColumns = [
    {
      key: "select", label: "", className: "w-10", hideMobile: true,
      render: (r) => (
        <Checkbox
          checked={picked.includes(r.product_id)}
          onCheckedChange={() => setPicked((p) => p.includes(r.product_id) ? p.filter((x) => x !== r.product_id) : [...p, r.product_id])}
          data-testid={`pick-${r.sku}`}
        />
      ),
    },
    {
      key: "product", label: "Product",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{r.product_name}</p>
          <p className="num truncate text-xs text-muted-foreground">{r.sku} . {r.category}</p>
        </div>
      ),
    },
    { key: "qty", label: "In stock", align: "right", render: (r) => <span className="num text-sm font-bold text-destructive">{r.qty}</span> },
    { key: "min_stock", label: "Min level", align: "right", render: (r) => <span className="num text-sm text-muted-foreground">{r.min_stock}</span>, hideMobile: true },
    { key: "suggested_qty", label: "Order qty", align: "right", render: (r) => <span className="num text-sm font-bold text-primary">{r.suggested_qty}</span> },
    { key: "supplier_name", label: "Supplier", render: (r) => <span className="text-sm">{r.supplier_name || "Not set"}</span> },
    { key: "estimated_value", label: "Est. cost", align: "right", render: (r) => <span className="num text-sm">{money(r.estimated_value)}</span> },
  ];

  const startPO = () => {
    const items = reorder.filter((r) => picked.includes(r.product_id));
    const supplier = items.find((i) => i.supplier_id) || {};
    const others = items.filter((i) => i.supplier_id && i.supplier_id !== supplier.supplier_id);
    sessionStorage.setItem("mv_po_draft", JSON.stringify({
      supplier_id: supplier.supplier_id || "",
      supplier_name: supplier.supplier_name || "",
      items: items.map((i) => ({
        product_id: i.product_id, name: i.product_name, description: "",
        qty: i.suggested_qty, price: i.purchase_cost, tax_rate: 18,
      })),
    }));
    if (others.length) {
      toast.warning("These products come from more than one supplier. Please review the supplier on the purchase order.");
    }
    navigate("/app/purchase-orders?new=1&reorder=1");
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Boxes}
        title="Inventory"
        subtitle="Live stock, movements and low stock alerts."
        actions={
          <>
            {can("inventory", "edit") && (
              <Button variant="success" onClick={() => setReceiveOpen(true)} data-testid="receive-btn">
                <PackageCheck className="h-4 w-4" /> Receive goods
              </Button>
            )}
            <Button variant="outline" onClick={() => setScanOpen(true)} data-testid="scan-btn">
              <ScanLine className="h-4 w-4" /> Scan barcode
            </Button>
            {rows.length > 0 && (
              <>
                <Button variant="outline" onClick={() => printBarcodes(rows, "Megatronix Vision", 1)} data-testid="print-stickers">
                  <Printer className="h-4 w-4" /> Print stickers
                </Button>
                <Button variant="outline" onClick={() => downloadBarcodeSheet(rows, "Megatronix Vision", 1)} data-testid="download-stickers">
                  <Barcode className="h-4 w-4" /> Download stickers
                </Button>
              </>
            )}
            {rows.length > 0 && (
              <Button variant="outline" onClick={() => {
                downloadCSV(rows.map((r) => ({
                  Product: r.product_name, SKU: r.sku, Category: r.category, Barcode: r.barcode,
                  Available: r.qty, Reserved: r.reserved, "Min Level": r.min_stock,
                  "Purchase Cost": r.purchase_cost, "Selling Price": r.selling_price, Supplier: r.supplier,
                })), "inventory.csv");
                toast.success("Inventory exported");
              }}><Download className="h-4 w-4" /> Export</Button>
            )}
          </>
        }
      />

      {negative.length > 0 && (
        <div className="flex flex-col gap-2 rounded-xl border border-destructive/50 bg-destructive-soft px-4 py-3" data-testid="negative-banner">
          <p className="flex items-center gap-2 text-sm font-semibold text-destructive">
            <TrendingDown className="h-5 w-5" />
            {negative.length} product{negative.length > 1 ? "s are" : " is"} in negative stock
          </p>
          <p className="text-xs text-foreground/70">
            These were invoiced before they were ever received. Receive the supplier bill or run a stock
            count to bring them back to a true quantity.
          </p>
          <div className="flex flex-wrap gap-2">
            {negative.map((n) => (
              <span key={n.product_id} className="num rounded-full border border-destructive/40 bg-card px-2.5 py-1 text-xs font-semibold text-destructive">
                {n.product_name} {n.qty}
              </span>
            ))}
          </div>
        </div>
      )}

      {lowCount > 0 && (
        <button onClick={() => setLowOnly(!lowOnly)}
          className={cn("flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
            lowOnly ? "border-destructive/40 bg-destructive-soft" : "border-destructive/25 bg-destructive-soft/60 hover:bg-destructive-soft")}>
          <PackageMinus className="h-5 w-5 text-destructive" />
          <span className="flex-1">
            <span className="block text-sm font-semibold text-destructive">{lowCount} product{lowCount > 1 ? "s" : ""} at or below minimum stock</span>
            <span className="block text-xs text-foreground/70">{lowOnly ? "Showing only low stock. Tap to show all." : "Tap to show only low stock items."}</span>
          </span>
        </button>
      )}

      <Tabs defaultValue="stock">
        <div className="scroll-slim -mx-1 max-w-full overflow-x-auto px-1">
          <TabsList className="w-max">
          <TabsTrigger value="stock" data-testid="tab-stock">Stock</TabsTrigger>
          <TabsTrigger value="reorder" data-testid="tab-reorder">
            Reorder
            {reorder.length > 0 && <span className="num ml-1.5 rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground">{reorder.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="moves" data-testid="tab-moves">Movement history</TabsTrigger>
          {can("stock_audit", "view") && (
            <TabsTrigger value="audits" data-testid="tab-audits">
              Stock audit
              {audits.filter((a) => a.status === "Pending Approval").length > 0 && (
                <span className="num ml-1.5 rounded-full bg-warning px-1.5 text-[10px] font-bold text-warning-foreground">
                  {audits.filter((a) => a.status === "Pending Approval").length}
                </span>
              )}
            </TabsTrigger>
          )}
        </TabsList>
        </div>

        <TabsContent value="stock" className="mt-5 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="inv-search"
                placeholder="Search by product, SKU or barcode" />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="sm:w-56"><SelectValue placeholder="All categories" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {loading ? <LoadingRows /> : (
            <DataTable columns={columns} rows={rows} rowClassName={(r) => r.qty < 0 && "bg-destructive-soft/60"}
              empty={<EmptyState icon={Boxes} title="No stock records" description="Add products from Admin Settings and they will appear here." />} />
          )}
        </TabsContent>

        <TabsContent value="reorder" className="mt-5 space-y-4">
          <div className="flex items-start gap-3 rounded-xl border border-primary/25 bg-primary-soft/50 px-4 py-3">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <p className="text-sm text-foreground/80">
              Suggested quantities bring each item back to roughly twice its minimum level.
              Tick what you want and we will start a purchase order with the supplier, products,
              cost and tax already filled in.
            </p>
          </div>

          {reorder.length === 0 ? (
            <EmptyState icon={ShoppingCart} title="Nothing to reorder"
              description="Every product is comfortably above its minimum stock level." />
          ) : (
            <>
              <DataTable columns={reorderColumns} rows={reorder} rowKey="product_id"
                empty={<EmptyState icon={ShoppingCart} title="Nothing to reorder" />} />
              {picked.length > 0 && (
                <div className="sticky bottom-4 z-30 flex flex-col gap-3 rounded-xl border border-border bg-card p-3 shadow-lg sm:flex-row sm:items-center">
                  <p className="flex-1 text-sm font-semibold">
                    {picked.length} product{picked.length > 1 ? "s" : ""} selected
                    <span className="ml-2 num font-normal text-muted-foreground">
                      approx {money(reorder.filter((r) => picked.includes(r.product_id)).reduce((a, r) => a + r.estimated_value, 0), true)}
                    </span>
                  </p>
                  <Button variant="ghost" size="sm" onClick={() => setPicked([])}>Clear</Button>
                  <Button onClick={startPO} data-testid="create-po-from-reorder">
                    <ShoppingCart className="h-4 w-4" /> Create purchase order
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="moves" className="mt-5">
          {loading ? <LoadingRows /> : (
            <DataTable columns={moveColumns} rows={moves}
              empty={<EmptyState icon={ArrowDownToLine} title="No stock movements yet"
                description="Every stock in and stock out will be recorded here with the staff member and reason." />} />
          )}
        </TabsContent>

        <TabsContent value="audits" className="mt-5">
          {can("stock_audit", "view") && <StockAuditPanel stock={rows} audits={audits} onDone={load} />}
        </TabsContent>

      </Tabs>

      {/* Stock move dialog */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{form.direction === "in" ? "Add stock" : "Issue stock"}</DialogTitle>
            <DialogDescription>Every movement is logged with your name, the reason and the previous stock level.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitMove} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Quantity" required>
                <Input type="number" min="1" required value={form.qty} data-testid="move-qty"
                  onChange={(e) => setForm({ ...form, qty: e.target.value })} />
              </Field>
              <Field label="Reason">
                <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(form.direction === "in" ? REASONS_IN : REASONS_OUT).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              {form.direction === "in" && (
                <Field label="Supplier">
                  <Select value={form.supplier} onValueChange={(v) => setForm({ ...form, supplier: v })}>
                    <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                    <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              )}
              <Field label="Reference (PO / Invoice no.)">
                <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
              </Field>
            </div>
            {form.direction === "in" && (
              <div className="space-y-3 rounded-xl border border-primary/25 bg-primary-soft/40 p-3">
                <Field label="Book against a purchase order" hint="Optional. The received quantity is written back to the PO line.">
                  <Select value={form.po_id || "none"} onValueChange={(v) => {
                    const po = openPos.find((p) => p.id === v);
                    setForm({
                      ...form, po_id: v === "none" ? "" : v,
                      supplier: po?.supplier_name || form.supplier,
                      reference: po?.number || form.reference,
                      reason: v === "none" ? form.reason : "Purchase order received",
                      mark_fully_received: v === "none" ? false : form.mark_fully_received,
                    });
                  }}>
                    <SelectTrigger data-testid="move-po"><SelectValue placeholder="No purchase order" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No purchase order</SelectItem>
                      {posForProduct.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.number} &middot; {p.supplier_name} &middot; {p.status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                {form.po_id && (
                  <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
                    <Checkbox checked={form.mark_fully_received} data-testid="move-mark-full"
                      onCheckedChange={(v) => setForm({ ...form, mark_fully_received: !!v })} />
                    Mark this purchase order fully received
                  </label>
                )}
              </div>
            )}
            {form.direction === "in" && (
              <Field label="Supplier invoice" hint="Attach the supplier invoice file name or reference.">
                <Textarea rows={2} value={form.invoice_file} onChange={(e) => setForm({ ...form, invoice_file: e.target.value })} />
              </Field>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMoveOpen(false)}>Cancel</Button>
              <Button type="submit" variant={form.direction === "in" ? "success" : "default"} disabled={busy} data-testid="move-save">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {form.direction === "in" ? "Add to stock" : "Issue stock"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Barcode scan dialog */}
      <ReceiveGoodsDialog open={receiveOpen} onOpenChange={setReceiveOpen} onDone={load} />

      <Dialog open={scanOpen} onOpenChange={(v) => { setScanOpen(v); if (!v) stopCam(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Scan or enter barcode</DialogTitle>
            <DialogDescription>
              Use a USB / bluetooth scanner, or type the barcode or SKU and press enter.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={scan} className="space-y-4">
            {camOn ? (
              <div className="relative overflow-hidden rounded-xl border border-primary/40 bg-foreground/90">
                <video ref={videoRef} playsInline muted className="h-52 w-full object-cover" />
                <div className="pointer-events-none absolute inset-x-8 top-1/2 h-0.5 -translate-y-1/2 bg-primary/80" />
                <p className="absolute bottom-2 left-0 right-0 text-center text-[11px] text-primary-foreground">
                  Hold the barcode inside the frame
                </p>
              </div>
            ) : (
              <div className="grid place-items-center rounded-xl border border-dashed border-primary/30 bg-primary-soft/40 py-8">
                <ScanLine className="h-10 w-10 animate-pulse text-primary" />
                <p className="mt-2 text-xs text-muted-foreground">Use a scanner, or scan with the camera</p>
              </div>
            )}
            <Button type="button" variant={camOn ? "outline" : "soft"} className="w-full"
              onClick={() => (camOn ? stopCam() : startCam())} data-testid="camera-toggle">
              {camOn ? <><CameraOff className="h-4 w-4" /> Stop camera</> : <><Camera className="h-4 w-4" /> Scan with camera</>}
            </Button>
            <Input autoFocus value={scanCode} onChange={(e) => setScanCode(e.target.value)}
              placeholder="8901234567890" className="num h-11 text-center tracking-widest" data-testid="scan-input" />
            <DialogFooter>
              <Button type="submit" className="w-full">Find product</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
